import { useEffect, useRef } from "react";

import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/cn";
import { strings } from "@/lib/strings";
import type { ApplyLogEntry, LogLevel } from "@/types/wizard";

const LEVEL_PREFIX: Record<LogLevel, string> = {
  info: "INFO",
  success: "OK",
  warn: "WARN",
  error: "ERR",
};

const LEVEL_TONE: Record<LogLevel, string> = {
  info: "text-muted-foreground",
  success: "text-success",
  warn: "text-warning",
  error: "text-destructive",
};

interface Props {
  logs: ApplyLogEntry[];
}

export function ApplyLogFeed({ logs }: Props) {
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
  }, [logs.length]);

  if (logs.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-border/60 bg-card/40 px-4 py-6 font-mono text-xs text-muted-foreground">
        {strings.pages.review.logsEmpty}
      </div>
    );
  }

  return (
    <ScrollArea className="h-64 rounded-lg border border-border/60 bg-background/60">
      <div ref={ref} className="flex flex-col gap-0.5 p-3 font-mono text-xs">
        {logs.map((entry, index) => (
          <div
            key={entry.id}
            className={cn(
              "flex flex-wrap items-baseline gap-2 rounded px-2 py-1",
              index === logs.length - 1 && "bg-secondary/40",
            )}
          >
            <span className="w-16 shrink-0 text-[10px] text-muted-foreground mono-num">{entry.time}</span>
            <span className={cn("w-10 shrink-0 font-semibold", LEVEL_TONE[entry.level])}>
              [{LEVEL_PREFIX[entry.level]}]
            </span>
            {entry.stage ? (
              <span className="shrink-0 rounded border border-border/60 px-1 text-[10px] uppercase text-muted-foreground">
                {entry.stage}
              </span>
            ) : null}
            <span className="text-foreground/90">{entry.message}</span>
          </div>
        ))}
      </div>
    </ScrollArea>
  );
}
